/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import api from '../utils/api';
import { peekNextNumber, advanceNumber } from '../utils/docNumbering';
import { useAuth } from '../contexts/AuthContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { hasPermission } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import RequestCancellationButton from '../components/RequestCancellationButton';
import FindDuplicatesButton from '../components/FindDuplicatesButton';
import DeliveryNotePrintView from '../components/delivery-notes/DeliveryNotePrintView';
import { resolvePreparedBy, resolveApprovers } from '../utils/signatures';

// Master-data fetch helpers — mirrors what SalesInvoices uses so the same
// Division / Category / Sub-Category / Brand / Model cascade works on DN lines.

const blankItem = () => ({
  product_id: '',
  product_name: '',
  part_number: '',
  description: '',
  brand: '',
  category: '',
  sub_category: '',
  division: '',
  model: '',
  quantity: 1,
  serial_numbers: [],
  warranty_months: 0,
});

const blankForm = () => ({
  dn_number: '',
  dn_date: new Date().toISOString().slice(0, 10),
  customer_id: '',
  customer_name: '',
  customer_po_no: '',
  customer_po_date: '',
  project: '',
  delivered_by: '',
  delivery_address: '',
  items: [blankItem()],
  notes: '',
});

export default function DeliveryNotes() {
  const { user } = useAuth();
  const { selectedFy } = useFiscalYear();
  const canCreate = hasPermission(user, 'delivery_notes', 'create');
  const canEdit = hasPermission(user, 'delivery_notes', 'edit');
  const canDelete = hasPermission(user, 'delivery_notes', 'delete');

  const [list, setList] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDN, setEditingDN] = useState(null);
  const [form, setForm] = useState(blankForm());
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [printing, setPrinting] = useState(null);
  const [serialChoicesByProduct, setSerialChoicesByProduct] = useState({});
  // Per-line search-term state for the Part Number and Search Product autocompletes.
  const [partNumberSearchTerm, setPartNumberSearchTerm] = useState({});
  const [showPartNumberDropdown, setShowPartNumberDropdown] = useState({});
  const [productSearchTerm, setProductSearchTerm] = useState({});
  const [showProductDropdown, setShowProductDropdown] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [dnRes, custRes, prodRes, divRes, catRes, subRes, brRes, mdRes, usrRes] = await Promise.all([
        api.get('/delivery-notes'),
        api.get('/customers'),
        api.get('/products'),
        api.get('/master-data/divisions').catch(() => ({ data: [] })),
        api.get('/master-data/categories').catch(() => ({ data: [] })),
        api.get('/master-data/subcategories').catch(() => ({ data: [] })),
        api.get('/master-data/brands').catch(() => ({ data: [] })),
        api.get('/master-data/models').catch(() => ({ data: [] })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setList(dnRes.data || []);
      setCustomers(custRes.data || []);
      setProducts(prodRes.data || []);
      setDivisions(divRes.data || []);
      setCategories(catRes.data || []);
      setSubcategories(subRes.data || []);
      setBrands(brRes.data || []);
      setModels(mdRes.data || []);
      setUsers(usrRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Close part/product autocompletes on outside click.
  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest('.dn-product-dropdown-container')) {
        setShowProductDropdown({});
      }
      if (!e.target.closest('.dn-part-number-dropdown-container')) {
        setShowPartNumberDropdown({});
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const fetchAvailableSerials = async (productId) => {
    if (!productId || serialChoicesByProduct[productId]) return;
    try {
      const res = await api.get(`/products/${productId}/available-serials`);
      // Endpoint returns either {items:[...]} or [...]; normalize to array of strings
      const raw = res.data?.items || res.data || [];
      const arr = raw.map((x) => (typeof x === 'string' ? x : x.serial_number));
      setSerialChoicesByProduct((m) => ({ ...m, [productId]: arr }));
    } catch (e) {
      console.error(e);
    }
  };

  const filtered = useMemo(() => {
    // Active Fiscal Year scope (selected globally in the header). When set,
    // hides any DN whose dn_date falls outside [start_date, end_date].
    const fyStart = selectedFy?.start_date || null;
    const fyEnd = selectedFy?.end_date || null;
    return (list || []).filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (fyStart || fyEnd) {
        const day = (d.dn_date || '').slice(0, 10);
        if (!day) return false;
        if (fyStart && day < fyStart) return false;
        if (fyEnd && day > fyEnd) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${d.dn_number} ${d.customer_name} ${d.customer_po_no || ''} ${d.project || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, statusFilter, search, selectedFy?.id]);

  const openCreate = () => {
    setEditingDN(null);
    setForm(blankForm());
    setShowForm(true);
    // Pre-fill the DN number from the company auto-numbering rule.
    peekNextNumber('delivery_note').then((n) => {
      if (n) setForm((prev) => prev.dn_number ? prev : { ...prev, dn_number: n });
    });
  };

  const openEdit = (dn) => {
    setEditingDN(dn);
    setForm({
      ...blankForm(),
      ...dn,
      items: (dn.items || []).map((i) => ({ ...blankItem(), ...i })),
    });
    setShowForm(true);
    // Pre-warm serial choices for each line's product
    for (const it of dn.items || []) {
      if (it.product_id) fetchAvailableSerials(it.product_id);
    }
  };

  const addItem = () => setForm((f) => ({ ...f, items: [...f.items, blankItem()] }));
  const removeItem = (idx) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, patch) =>
    setForm((f) => ({
      ...f,
      items: f.items.map((it, i) => (i === idx ? { ...it, ...patch } : it)),
    }));

  const pickProductForLine = (idx, productId) => {
    const p = products.find((x) => x.id === productId);
    if (!p) {
      updateItem(idx, { product_id: '', product_name: '', part_number: '', brand: '', category: '', sub_category: '', division: '', model: '', description: '' });
      return;
    }
    updateItem(idx, {
      product_id: p.id,
      product_name: p.name,
      part_number: p.part_number || '',
      brand: p.brand || '',
      category: p.category || '',
      sub_category: p.sub_category || '',
      division: p.division || '',
      model: p.model || '',
      description: p.description || p.specifications || '',
      serial_numbers: [],
    });
    fetchAvailableSerials(p.id);
  };

  // ── Part Number autocomplete ──────────────────────────────────────────────
  const handlePartNumberSearch = (idx, value) => {
    setPartNumberSearchTerm((m) => ({ ...m, [idx]: value }));
    setShowPartNumberDropdown((m) => ({ ...m, [idx]: true }));
    updateItem(idx, { part_number: value });
  };
  const handlePartNumberSelect = (idx, product) => {
    setPartNumberSearchTerm((m) => ({ ...m, [idx]: product.part_number }));
    setProductSearchTerm((m) => ({ ...m, [idx]: product.name }));
    setShowPartNumberDropdown((m) => ({ ...m, [idx]: false }));
    pickProductForLine(idx, product.id);
  };
  const getFilteredProductsByPartNumber = (idx) => {
    const q = (partNumberSearchTerm[idx] ?? form.items[idx]?.part_number ?? '').toLowerCase();
    if (!q) return [];
    return (products || []).filter((p) => (p.part_number || '').toLowerCase().includes(q)).slice(0, 30);
  };

  // ── Product Name autocomplete ─────────────────────────────────────────────
  const handleProductSearch = (idx, value) => {
    setProductSearchTerm((m) => ({ ...m, [idx]: value }));
    setShowProductDropdown((m) => ({ ...m, [idx]: true }));
    updateItem(idx, { product_name: value });
  };
  const handleProductSelect = (idx, product) => {
    setProductSearchTerm((m) => ({ ...m, [idx]: product.name }));
    setPartNumberSearchTerm((m) => ({ ...m, [idx]: product.part_number || '' }));
    setShowProductDropdown((m) => ({ ...m, [idx]: false }));
    pickProductForLine(idx, product.id);
  };
  const getFilteredProducts = (idx) => {
    const q = (productSearchTerm[idx] ?? form.items[idx]?.product_name ?? '').toLowerCase();
    if (!q) return [];
    return (products || []).filter((p) => (p.name || '').toLowerCase().includes(q)).slice(0, 30);
  };

  const handleItemFilterChange = (idx, field, value) => {
    // Mirror of SalesInvoices' handleItemChange for filter dropdowns —
    // cascading resets when a parent changes.
    const patch = { [field]: value };
    if (field === 'division') { patch.category = ''; patch.sub_category = ''; patch.brand = ''; patch.model = ''; }
    if (field === 'category') { patch.sub_category = ''; patch.brand = ''; patch.model = ''; }
    if (field === 'sub_category') { patch.brand = ''; patch.model = ''; }
    if (field === 'brand') { patch.model = ''; }
    updateItem(idx, patch);
  };

  const toggleSerial = (idx, serial) => {
    const cur = form.items[idx].serial_numbers || [];
    const next = cur.includes(serial)
      ? cur.filter((s) => s !== serial)
      : [...cur, serial];
    updateItem(idx, { serial_numbers: next, quantity: Math.max(next.length, 1) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.dn_number.trim()) return alert('DN number is required');
    if (!form.customer_id) return alert('Pick a customer');
    if (!form.items.length || !form.items[0].product_name) return alert('Add at least one item');
    try {
      const payload = {
        ...form,
        items: form.items.map((it) => ({
          ...it,
          quantity: parseFloat(it.quantity) || 0,
          warranty_months: parseInt(it.warranty_months, 10) || 0,
        })),
      };
      if (editingDN) {
        await api.put(`/delivery-notes/${encodeURIComponent(editingDN.dn_number)}`, payload);
      } else {
        await api.post('/delivery-notes', payload);
        advanceNumber('delivery_note', payload.dn_number);
      }
      setShowForm(false);
      setEditingDN(null);
      setForm(blankForm());
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const handleDelete = async (dn) => {
    if (!window.confirm(`Delete DN ${dn.dn_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/delivery-notes/${encodeURIComponent(dn.dn_number)}`);
      await fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || err.message);
    }
  };

  const customerById = (id) => customers.find((c) => c.id === id);

  return (
    <div className="w-screen max-w-full overflow-x-hidden overflow-y-auto">
      <div className="w-full px-2 sm:px-4 lg:px-6 py-3 sm:py-6 overflow-x-hidden max-w-full">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
          <h1 className="text-2xl font-bold">🚚 Delivery Notes</h1>
          <div className="flex gap-2 flex-wrap">
            <FindDuplicatesButton
              module="sales-invoices"
              labelFields={['invoice_number']}
            />
            {canCreate && (
              <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="add-dn-btn">
                + New Delivery Note
              </Button>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search DN# / customer / PO / project"
                className="border rounded px-3 py-2 text-sm md:col-span-2"
                data-testid="dn-search"
              />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="border rounded px-3 py-2 text-sm"
                data-testid="dn-status-filter"
              >
                <option value="all">All Statuses</option>
                <option value="open">Open (not invoiced)</option>
                <option value="invoiced">Invoiced</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <div className="text-sm text-gray-600 flex items-center">
                Showing <b className="px-1">{filtered.length}</b> of {list.length}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* List */}
        <Card>
          <CardHeader>
            <CardTitle>All Delivery Notes</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-8 text-center text-gray-500">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No delivery notes yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left">DN #</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-left">Customer</th>
                      <th className="px-3 py-2 text-left">Customer PO</th>
                      <th className="px-3 py-2 text-left">Project</th>
                      <th className="px-3 py-2 text-center">Items</th>
                      <th className="px-3 py-2 text-center">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((dn) => (
                      <tr key={dn.dn_number} className={`border-t ${dn.cancellation_status === 'pending' ? 'bg-amber-50 hover:bg-amber-100 ring-1 ring-amber-200' : 'hover:bg-blue-50/30'}`} data-testid={`dn-row-${dn.dn_number}`}>
                        <td className="px-3 py-2 font-medium">{dn.dn_number}</td>
                        <td className="px-3 py-2">{formatDateDMY(dn.dn_date)}</td>
                        <td className="px-3 py-2">{dn.customer_name}</td>
                        <td className="px-3 py-2">{dn.customer_po_no || '—'}</td>
                        <td className="px-3 py-2">{dn.project || '—'}</td>
                        <td className="px-3 py-2 text-center">{(dn.items || []).length}</td>
                        <td className="px-3 py-2 text-center">
                          <span
                            className={
                              dn.status === 'open'
                                ? 'inline-block px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800'
                                : dn.status === 'invoiced'
                                ? 'inline-block px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800'
                                : 'inline-block px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-700'
                            }
                          >
                            {dn.status === 'invoiced'
                              ? `✓ Invoiced (${dn.invoice_number || '?'})`
                              : dn.status === 'open'
                              ? 'Open'
                              : 'Cancelled'}
                          </span>
                          {dn.cancellation_status === 'pending' && (
                            <span className="ml-1 inline-block px-1.5 py-0.5 rounded text-[10px] bg-amber-100 text-amber-800 font-semibold" title={`Cancellation pending — ${dn.cancellation_reason || ''}`}>
                              🟠 Cancel pending
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button
                            onClick={() => setViewing(dn)}
                            className="text-blue-700 hover:underline mr-2"
                            data-testid={`dn-view-${dn.dn_number}`}
                          >👁 View</button>
                          <button
                            onClick={() => setPrinting(dn)}
                            className="text-purple-700 hover:underline mr-2"
                            data-testid={`dn-print-${dn.dn_number}`}
                          >🖨 Print</button>
                          {canEdit && dn.status !== 'invoiced' && (
                            <button
                              onClick={() => openEdit(dn)}
                              className="text-amber-700 hover:underline mr-2"
                              data-testid={`dn-edit-${dn.dn_number}`}
                            >✏️ Edit</button>
                          )}
                          {canDelete && dn.status !== 'invoiced' && (
                            <button
                              onClick={() => handleDelete(dn)}
                              className="text-red-700 hover:underline mr-2"
                              data-testid={`dn-delete-${dn.dn_number}`}
                            >🗑 Delete</button>
                          )}
                          <RequestCancellationButton docKind="delivery_note" docId={dn.dn_number} doc={dn} onDone={fetchAll} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[92vh] overflow-y-auto"
            data-testid="dn-form"
          >
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-semibold text-lg">
                {editingDN ? `✏️ Edit Delivery Note ${editingDN.dn_number}` : '🚚 New Delivery Note'}
              </h3>
              <button
                type="button"
                onClick={() => { setShowForm(false); setEditingDN(null); }}
                className="text-gray-500 hover:text-gray-700"
              >✕</button>
            </div>

            <div className="p-5 space-y-4">
              {/* Header fields */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">DN Number *</label>
                  <input
                    value={form.dn_number}
                    onChange={(e) => setForm({ ...form, dn_number: e.target.value })}
                    disabled={!!editingDN}
                    className="w-full border rounded px-3 py-2 text-sm disabled:bg-gray-100"
                    placeholder="e.g. DN-2026-001"
                    data-testid="dn-number-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">DN Date *</label>
                  <DateInputDMY
                    value={form.dn_date}
                    onChange={(e) => setForm({ ...form, dn_date: e.target.value })}
                    data-testid="dn-date-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Customer *</label>
                  <SearchableSelect
                    options={customers.map((c) => ({ value: c.id, label: c.name }))}
                    value={form.customer_id}
                    onChange={(v) => {
                      const c = customers.find((x) => x.id === v);
                      setForm({
                        ...form,
                        customer_id: v,
                        customer_name: c?.name || '',
                        delivery_address: c?.address || form.delivery_address,
                      });
                    }}
                    placeholder="Select customer"
                    data-testid="dn-customer-select"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Customer PO No</label>
                  <input
                    value={form.customer_po_no}
                    onChange={(e) => setForm({ ...form, customer_po_no: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                    data-testid="dn-po-no-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Customer PO Date</label>
                  <DateInputDMY
                    value={form.customer_po_date}
                    onChange={(e) => setForm({ ...form, customer_po_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Project</label>
                  <input
                    value={form.project}
                    onChange={(e) => setForm({ ...form, project: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Delivered By</label>
                  <input
                    value={form.delivered_by}
                    onChange={(e) => setForm({ ...form, delivered_by: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-xs font-semibold text-gray-700">Delivery Address</label>
                  <input
                    value={form.delivery_address}
                    onChange={(e) => setForm({ ...form, delivery_address: e.target.value })}
                    className="w-full border rounded px-3 py-2 text-sm"
                    placeholder="Defaults to customer's address"
                  />
                </div>
              </div>

              {/* Items — matches Sales Invoice line layout (Part Number search,
                  Product search, Division → Category → Sub-Category → Brand
                  → Model dropdowns, Qty), but NO price columns. */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-semibold">Items (Quantity Only — No Prices)</h4>
                  <button
                    type="button"
                    onClick={addItem}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm px-3 py-1 rounded"
                    data-testid="dn-add-item"
                  >+ Add Item</button>
                </div>
                <div className="space-y-3">
                  {form.items.map((it, idx) => {
                    const serials = serialChoicesByProduct[it.product_id] || [];
                    const selectedSerials = it.serial_numbers || [];
                    const remainingSerials = serials.filter((s) => !selectedSerials.includes(s));
                    return (
                      <div
                        key={`dn-item-row-${idx}`}
                        className="border border-gray-200 rounded p-3 bg-gray-50"
                        data-testid={`dn-item-${idx}`}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-8 gap-2">
                          {/* Part Number — searchable */}
                          <div className="relative dn-part-number-dropdown-container">
                            <input
                              type="text"
                              placeholder="Part Number"
                              value={partNumberSearchTerm[idx] ?? it.part_number ?? ''}
                              onChange={(e) => handlePartNumberSearch(idx, e.target.value)}
                              onFocus={() => setShowPartNumberDropdown((m) => ({ ...m, [idx]: true }))}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              data-testid={`dn-item-part-${idx}`}
                            />
                            {showPartNumberDropdown[idx] && getFilteredProductsByPartNumber(idx).length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {getFilteredProductsByPartNumber(idx).map((p) => (
                                  <div
                                    key={p.id}
                                    onClick={() => handlePartNumberSelect(idx, p)}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                                  >
                                    <div className="font-medium">{p.part_number}</div>
                                    <div className="text-xs text-gray-500">
                                      {p.name}
                                      {p.category && ` • ${p.category}`}
                                      {p.brand && ` • ${p.brand}`}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Search Product */}
                          <div className="md:col-span-2 relative dn-product-dropdown-container">
                            <input
                              type="text"
                              placeholder="Search Product *"
                              value={productSearchTerm[idx] ?? it.product_name ?? ''}
                              onChange={(e) => handleProductSearch(idx, e.target.value)}
                              onFocus={() => setShowProductDropdown((m) => ({ ...m, [idx]: true }))}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              required
                              data-testid={`dn-item-product-${idx}`}
                            />
                            {showProductDropdown[idx] && getFilteredProducts(idx).length > 0 && (
                              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                {getFilteredProducts(idx).map((p) => (
                                  <div
                                    key={p.id}
                                    onClick={() => handleProductSelect(idx, p)}
                                    className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm border-b border-gray-100"
                                  >
                                    <div className="font-medium">{p.name}</div>
                                    <div className="text-xs text-gray-500">
                                      {p.part_number && `Part #: ${p.part_number}`}
                                      {p.category && ` • ${p.category}`}
                                      {p.brand && ` • ${p.brand}`}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Division */}
                          <div>
                            <select
                              value={it.division || ''}
                              onChange={(e) => handleItemFilterChange(idx, 'division', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Division</option>
                              {divisions.map((d) => (
                                <option key={d.name} value={d.name}>{d.name}</option>
                              ))}
                            </select>
                          </div>

                          {/* Category */}
                          <div>
                            <select
                              value={it.category || ''}
                              onChange={(e) => handleItemFilterChange(idx, 'category', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Category</option>
                              {categories
                                .filter((c) => !it.division || c.parent_division === it.division)
                                .map((c) => (
                                  <option key={c.name} value={c.name}>{c.name}</option>
                                ))}
                            </select>
                          </div>

                          {/* Sub-Category */}
                          <div>
                            <select
                              value={it.sub_category || ''}
                              onChange={(e) => handleItemFilterChange(idx, 'sub_category', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                            >
                              <option value="">Sub-Category</option>
                              {subcategories
                                .filter((s) => !it.category || s.parent_category === it.category)
                                .map((s) => (
                                  <option key={s.name} value={s.name}>{s.name}</option>
                                ))}
                            </select>
                          </div>

                          {/* Brand */}
                          <div>
                            <select
                              value={it.brand || ''}
                              onChange={(e) => handleItemFilterChange(idx, 'brand', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              disabled={!it.category}
                            >
                              <option value="">Brand</option>
                              {brands
                                .filter((b) => it.category && b.parent_category === it.category)
                                .map((b) => (
                                  <option key={b.name} value={b.name}>{b.name}</option>
                                ))}
                            </select>
                          </div>

                          {/* Model */}
                          <div>
                            <select
                              value={it.model || ''}
                              onChange={(e) => handleItemFilterChange(idx, 'model', e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              disabled={!it.brand}
                            >
                              <option value="">Model</option>
                              {models
                                .filter((m) => it.brand && m.parent_brand === it.brand)
                                .map((m) => (
                                  <option key={m.name} value={m.name}>{m.name}</option>
                                ))}
                            </select>
                          </div>

                          {/* Quantity */}
                          <div>
                            <input
                              type="number"
                              placeholder="Qty"
                              value={it.quantity}
                              onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              min="0.01"
                              step="0.01"
                              required
                              data-testid={`dn-item-qty-${idx}`}
                            />
                          </div>
                        </div>

                        {/* Row 2: Warranty + Remove (NO price/total) */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mt-2">
                          <div>
                            <input
                              type="number"
                              placeholder="Warranty (months)"
                              value={it.warranty_months ?? 0}
                              onChange={(e) => updateItem(idx, { warranty_months: parseInt(e.target.value, 10) || 0 })}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              min="0"
                              step="1"
                              title="Customer warranty in months from delivery date"
                              data-testid={`dn-item-warranty-${idx}`}
                            />
                          </div>
                          <div className="md:col-span-2">
                            <input
                              type="text"
                              placeholder="Description / notes"
                              value={it.description || ''}
                              onChange={(e) => updateItem(idx, { description: e.target.value })}
                              className="w-full p-2 border border-gray-300 rounded text-sm"
                              data-testid={`dn-item-desc-${idx}`}
                            />
                          </div>
                          <div>
                            {form.items.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeItem(idx)}
                                className="w-full px-3 py-2 text-sm text-red-700 border border-red-600 rounded hover:bg-red-50"
                              >
                                Remove Item
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Serial Numbers (same UX as Sales Invoice) */}
                        {it.product_id && (
                          <div className="mt-3 p-2 rounded border border-blue-200 bg-blue-50/40" data-testid={`dn-serials-block-${idx}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-semibold text-gray-700">
                                Serial Numbers — {serials.length} in stock
                              </span>
                              {selectedSerials.length > 0 && (
                                <span className="text-xs text-blue-700">
                                  Selected: <b>{selectedSerials.length}</b>
                                </span>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <select
                                value=""
                                disabled={remainingSerials.length === 0}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  if (v) toggleSerial(idx, v);
                                }}
                                className="flex-1 min-w-[200px] px-2 py-1 border rounded text-sm"
                                data-testid={`dn-serial-select-${idx}`}
                              >
                                <option value="">
                                  {remainingSerials.length === 0
                                    ? (serials.length === 0 ? '— No stock — add via Purchase Entries —' : '— All in-stock serials added —')
                                    : `+ Add serial number (${remainingSerials.length} available)`}
                                </option>
                                {remainingSerials.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                              {selectedSerials.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => updateItem(idx, { serial_numbers: [] })}
                                  className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
                                  data-testid={`dn-serials-clear-${idx}`}
                                >
                                  Clear
                                </button>
                              )}
                            </div>
                            {selectedSerials.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1.5">
                                {selectedSerials.map((s) => (
                                  <span
                                    key={s}
                                    className="text-xs px-2 py-1 rounded bg-blue-600 text-white inline-flex items-center gap-1"
                                    data-testid={`dn-serial-chip-${idx}-${s}`}
                                  >
                                    {s}
                                    <button
                                      type="button"
                                      onClick={() => toggleSerial(idx, s)}
                                      className="text-white/80 hover:text-white"
                                      title="Remove this serial"
                                      aria-label={`Remove ${s}`}
                                    >
                                      ✕
                                    </button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-xs font-semibold text-gray-700">Notes</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full border rounded px-3 py-2 text-sm"
                  rows={2}
                />
              </div>
            </div>

            <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2 sticky bottom-0">
              <Button
                type="button"
                onClick={() => { setShowForm(false); setEditingDN(null); }}
                variant="outline"
              >Cancel</Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="dn-save-btn">
                {editingDN ? '💾 Update DN' : '💾 Save Delivery Note'}
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* View Modal */}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[92vh] overflow-y-auto">
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-lg">🚚 Delivery Note — {viewing.dn_number}</h3>
              <button onClick={() => setViewing(null)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              {viewing.cancellation_status === 'pending' && (
                <div className="rounded border border-amber-300 bg-amber-50 px-3 py-2" data-testid={`cancel-pending-banner-${viewing.dn_number}`}>
                  <div className="font-semibold text-amber-900">🟠 Cancellation pending approval</div>
                  <div className="text-amber-800 mt-1"><span className="font-medium">Reason:</span> {viewing.cancellation_reason || '—'}</div>
                  {viewing.cancellation_note && (
                    <div className="text-amber-800"><span className="font-medium">Note:</span> {viewing.cancellation_note}</div>
                  )}
                  {viewing.cancellation_requested_by_name && (
                    <div className="text-xs text-amber-700 mt-1">Requested by {viewing.cancellation_requested_by_name}{viewing.cancellation_requested_at ? ` • ${formatDateDMY(viewing.cancellation_requested_at)}` : ''}</div>
                  )}
                </div>
              )}
              {viewing.cancellation_status === 'cancelled' && (
                <div className="rounded border border-slate-300 bg-slate-100 px-3 py-2" data-testid={`cancel-done-banner-${viewing.dn_number}`}>
                  <div className="font-semibold text-slate-800">🚫 Delivery Note cancelled</div>
                  {viewing.cancellation_reason && (
                    <div className="text-slate-700 mt-1"><span className="font-medium">Reason:</span> {viewing.cancellation_reason}</div>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><b>Date:</b> {formatDateDMY(viewing.dn_date)}</div>
                <div><b>Status:</b> {viewing.status}{viewing.invoice_number ? ` → ${viewing.invoice_number}` : ''}</div>
                <div><b>Customer:</b> {viewing.customer_name}</div>
                <div><b>Customer PO:</b> {viewing.customer_po_no || '—'}</div>
                <div><b>Project:</b> {viewing.project || '—'}</div>
                <div><b>Delivered By:</b> {viewing.delivered_by || '—'}</div>
                <div className="col-span-2"><b>Address:</b> {viewing.delivery_address || customerById(viewing.customer_id)?.address || '—'}</div>
              </div>
              <div>
                <h4 className="font-semibold mb-1">Items</h4>
                <table className="w-full text-xs border">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left">#</th>
                      <th className="px-2 py-1 text-left">Part #</th>
                      <th className="px-2 py-1 text-left">Product</th>
                      <th className="px-2 py-1 text-left">Description</th>
                      <th className="px-2 py-1 text-right">Qty</th>
                      <th className="px-2 py-1 text-left">Serial #s</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewing.items || []).map((it, i) => (
                      <tr key={`v-${i}`} className="border-t">
                        <td className="px-2 py-1">{i + 1}</td>
                        <td className="px-2 py-1">{it.part_number || '—'}</td>
                        <td className="px-2 py-1">{it.product_name}</td>
                        <td className="px-2 py-1">{it.description || '—'}</td>
                        <td className="px-2 py-1 text-right">{it.quantity}</td>
                        <td className="px-2 py-1">{(it.serial_numbers || []).join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {viewing.notes && (
                <div><b>Notes:</b> {viewing.notes}</div>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" onClick={() => { setPrinting(viewing); }}>🖨 Print</Button>
              <Button onClick={() => setViewing(null)} className="bg-blue-600 hover:bg-blue-700">Close</Button>
            </div>
          </div>
        </div>
      )}

      {/* Print View */}
      {printing && (
        <DeliveryNotePrintView
          dn={printing}
          customer={customerById(printing.customer_id)}
          preparedBy={resolvePreparedBy(printing, users)}
          approvers={resolveApprovers(printing, users)}
          onDone={() => setPrinting(null)}
        />
      )}
    </div>
  );
}
